import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap';
import { FaTags, FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import colors from '../constants/colors';

const LabelManagement = () => {
  const [labels, setLabels] = useState([]);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3498db');
  const [editingLabel, setEditingLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchLabels();
  }, []);

  const fetchLabels = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-labels`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setLabels(response.data.data || []);
    } catch (error) {
      console.error('Error fetching labels', error);
      setError('Failed to load labels. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrUpdateLabel = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Label name is required.');
      return;
    }

    setSaving(true);
    try {
      const payload = { name, color };
      if (editingLabel) {
        payload.id = editingLabel.id;
      }

      const url = editingLabel
        ? `${appConstants.API_ROOT_URL}update-label`
        : `${appConstants.API_ROOT_URL}create-label`;

      await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      setName('');
      setColor('#3498db');
      setEditingLabel(null);
      setShowModal(false);
      fetchLabels();
    } catch (error) {
      console.error('Error saving label', error);
      alert('Failed to save label. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (label) => {
    setEditingLabel(label);
    setName(label.name);
    setColor(label.color);
    setShowModal(true);
  };

  const handleShowModal = () => {
    setEditingLabel(null);
    setName('');
    setColor('#3498db');
    setShowModal(true);
  };

  const handleCancelEdit = () => {
    setEditingLabel(null);
    setName('');
    setColor('#3498db');
    setShowModal(false);
  };

  // Filter labels based on search term
  const filteredLabels = labels.filter((label) =>
    label.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this label?')) return;
    try {
      await axios.post(
        `${appConstants.API_ROOT_URL}delete-label`,
        { id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      fetchLabels();
    } catch (error) {
      console.error('Error deleting label', error);
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Compact Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '12px',
            backgroundColor: colors.sidebar.background,
            padding: '6px',
            borderRadius: '6px',
            color: colors.sidebar.text,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: '1rem',
                color: 'white',
                marginRight: 16,
              }}
            >
              <FaTags />
              Labels
            </span>

            {/* Search Input */}
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.sidebar.text,
                  fontSize: '0.9rem',
                }}
              />
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search labels..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  paddingLeft: '35px',
                  height: '30px',
                  backgroundColor: colors.card.background,
                  borderColor: colors.card.border,
                  color: colors.text.primary,
                }}
              />
            </div>
          </div>

          {/* Add Label Button */}
          <Button
            onClick={handleShowModal}
            variant="light"
            size="sm"
            disabled={saving}
            style={{
              width: '32px',
              height: '28px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backgroundColor: colors.button.secondary,
              borderColor: colors.button.secondary,
              color: colors.text.primary,
              fontSize: '0.85rem',
              fontWeight: '600',
            }}
            title="Add Label"
          >
            <FaPlus />
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            variant="danger"
            onClose={() => setError('')}
            dismissible
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Content Section */}
        {loading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Spinner
              animation="border"
              style={{ color: '#667eea', width: '50px', height: '50px' }}
            />
            <p style={{ marginTop: '20px', color: '#6c757d' }}>
              Loading labels...
            </p>
          </div>
        ) : filteredLabels.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <FaTags
              style={{
                fontSize: '4rem',
                color: '#dee2e6',
                marginBottom: '20px',
              }}
            />
            <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>
              No Labels Found
            </h4>
            <p style={{ color: '#adb5bd' }}>
              {searchTerm
                ? 'No labels match your search criteria.'
                : 'Get started by creating your first label.'}
            </p>
            {!searchTerm && (
              <Button
                onClick={handleShowModal}
                style={{
                  marginTop: '20px',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                }}
              >
                <FaPlus className="me-2" /> Create Label
              </Button>
            )}
          </div>
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <Table hover style={{ marginBottom: 0 }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: 'white',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '80px',
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                      }}
                    >
                      Label
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                        width: '120px',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLabels.map((label, index) => (
                    <tr
                      key={label.id}
                      style={{
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#6c757d',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                          }}
                        >
                          <div
                            style={{
                              width: '24px',
                              height: '24px',
                              backgroundColor: label.color,
                              borderRadius: '6px',
                              border: '2px solid #fff',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          />
                          <span
                            style={{
                              color: '#2c3e50',
                              fontWeight: '500',
                              fontSize: '0.9rem',
                            }}
                          >
                            {label.name}
                          </span>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            justifyContent: 'center',
                          }}
                        >
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(label)}
                            style={{
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '0.85rem',
                            }}
                            title="Edit Label"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(label.id)}
                            style={{
                              borderRadius: '6px',
                              padding: '6px 10px',
                              fontSize: '0.85rem',
                            }}
                            title="Delete Label"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Summary Footer */}
            <div
              style={{
                padding: '16px 20px',
                background: '#f8f9fa',
                borderTop: '2px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px',
              }}
            >
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                Showing {filteredLabels.length} labels
                {searchTerm && ' (filtered)'}
              </span>
            </div>
          </div>
        )}

        {/* Add/Edit Label Modal */}
        <Modal show={showModal} onHide={handleCancelEdit} centered>
          <Modal.Header
            closeButton
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderBottom: 'none',
            }}
          >
            <Modal.Title style={{ fontWeight: '700' }}>
              {editingLabel ? 'Edit Label' : 'Add New Label'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '30px' }}>
            <Form onSubmit={handleAddOrUpdateLabel}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Label Name <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter label name"
                  required
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    padding: '10px 15px',
                    transition: 'all 0.3s ease',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.boxShadow =
                      '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#dee2e6';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Color <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '15px' }}
                >
                  <Form.Control
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    required
                    style={{
                      width: '60px',
                      height: '40px',
                      borderRadius: '8px',
                      border: '2px solid #dee2e6',
                      padding: '2px',
                      cursor: 'pointer',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Form.Control
                      type="text"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      placeholder="#3498db"
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '10px 15px',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: color,
                      borderRadius: '8px',
                      border: '2px solid #fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                  />
                </div>
                <Form.Text className="text-muted">
                  Choose a color or enter a hex code
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer
            style={{ borderTop: '1px solid #dee2e6', padding: '20px 30px' }}
          >
            <Button
              variant="secondary"
              onClick={handleCancelEdit}
              disabled={saving}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddOrUpdateLabel}
              disabled={saving || !name.trim()}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
              }}
            >
              {saving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                <>{editingLabel ? 'Update Label' : 'Create Label'}</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Layout>
  );
};

export default LabelManagement;
